import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import Header from "../components/Header";
import SideNav from "../components/SideNav";
import useHasPermission from "../hooks/useHasPermission";
import { PERMISSIONS } from "../constants/permissions";
import { useParams } from "react-router";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Loader from "../components/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchAllTargetSavings,
  fetchOfficers,
  setCustomerLimit,
} from "../actions/adminActions";
import { format, formatDate } from "../main";
import {
  assignOfficerToCustomer,
  customerDeactivate,
  fetchCustomerCards,
  getCustomerDetails,
  removeCustomerCard,
  updateCustomerName,
} from "../actions/userActions";
import CustomerTransactions from "../components/CustomerTransactions";
import { Link } from "react-router-dom";
import CustomerDeactivateModal from "../components/CustomerDeactivateModal";
import { fetchCustomerBusiness } from "../actions/merchantActions";
import BusinessWallet from "../components/BusinessWallet";
import { BASE_API_URL } from "../constants/dropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const UserScreen = () => {
  const { id } = useParams();

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [restrictionType, setRestrictionType] = useState(0);
  const [liftRestriction, setLiftRestriction] = useState(false);
  const [restrictionLoading, setRestrictionLoading] = useState(false);

  const customerDetails = useSelector((state) => state.customerDetails);
  const { loading, error, success, customer } = customerDetails;

  const getOfficers = useSelector((state) => state.getOfficers);
  const { officers } = getOfficers;

  const customerCard = useSelector((state) => state.customerCard);
  const { loading: loadingCard, success: successCard, cards } = customerCard;

  const assignedOfficer = useSelector((state) => state.assignedOfficer);
  const { loading: officerLoading, success: officerSuccess } = assignedOfficer;

  const admin2faLogin = useSelector((state) => state.admin2faLogin);
  const { userInfo } = admin2faLogin;
  const canViewCustomerActions = useHasPermission(PERMISSIONS.VIEW_CUSTOMER_ACTIONS);
  const canUpdateCustomerNames = useHasPermission(PERMISSIONS.UPDATE_CUSTOMER_NAMES);
  const canGenerateStatement = useHasPermission(PERMISSIONS.GENERATE_CUSTOMER_STATEMENT);

  const [statementLoading, setStatementLoading] = useState(false);
  const [statementFromDate, setStatementFromDate] = useState(() => {
    const d = new Date(); d.setMonth(0); d.setDate(1); return d;
  });
  const [statementToDate, setStatementToDate] = useState(new Date());

  const [newName, setNewName] = useState("");
  const [nameModal, setNameModal] = useState(false);
  const [statementModal, setStatementModal] = useState(false);
  const updateNameState = useSelector((state) => state.updateCustomerName);
  const { loading: nameLoading, success: nameSuccess, error: nameError } = updateNameState;

  const deleteCustomerCard = useSelector((state) => state.deleteCustomerCard);
  const {
    loading: deleteCardLoading,
    success: deleteCardSuccess,
    error: deleteCardError,
  } = deleteCustomerCard;

  const deactivateCustomer = useSelector((state) => state.deactivateCustomer);
  const { loading: deactivateLoading, success: deactivateSuccess } =
    deactivateCustomer;

  const allTargetSavings = useSelector((state) => state.allTargetSavings);
  const {
    loading: savingsLoading,
    success: savingsSuccess,
    alltargetSavings: savings,
  } = allTargetSavings;

  const customerLimit = useSelector((state) => state.customerLimit);
  const {
    success: limitSuccess,
    error: limitError,
    loading: limitLoading,
  } = customerLimit;

  const getCustomerBusiness = useSelector((state) => state.getCustomerBusiness);
  const {
    loading: businessLoading,
    success: businessSuccess,
    business,
  } = getCustomerBusiness;

  const customerRef = useRef(true);
  const customerInformation = customer?.data?.customer_information;
  const isPnd = Number(customerInformation?.customer_tier_id) === 0;
  const isBlocked = Number(customerInformation?.status_id) === 4;

  useEffect(() => {
    // if (
    //   (!loading && error === 'Please login again') ||
    //   error === 'You have previously logged out. Please login again'
    // ) {
    //   localStorage.removeItem('userInfo')
    //   window.location = `${process.env.REACT_APP_BASE_URL}/`
    // }
    if (customerRef.current) {
      const filterData = {
        customerid: id,
        statusid: 2,
        byBalance: true,
      };
      dispatch(getCustomerDetails(id));
      dispatch(fetchCustomerBusiness(id));
      dispatch(fetchOfficers());
      dispatch(fetchCustomerCards(id));
      dispatch(fetchAllTargetSavings(filterData));
      customerRef.current = false;
    }
    if (officerSuccess) {
      window.location.reload();
    }
    if (deleteCardSuccess) {
      window.location.reload();
    }
    if (deactivateSuccess) {
      window.location.reload();
    }
    if (limitSuccess) {
      window.location.reload();
    }
    if (nameSuccess) {
      toast.success("Customer name updated successfully", { position: toast.POSITION.TOP_RIGHT });
      dispatch(getCustomerDetails(id));
      setNewName("");
      setNameModal(false);
      dispatch({ type: "UPDATE_CUSTOMER_NAME_RESET" });
    }
    if (nameError) {
      toast.error(nameError, { position: toast.POSITION.TOP_RIGHT });
      dispatch({ type: "UPDATE_CUSTOMER_NAME_RESET" });
    }
  }, [
    id,
    dispatch,
    loading,
    error,
    officerSuccess,
    deleteCardSuccess,
    deactivateSuccess,
    customer,
    limitSuccess,
    nameSuccess,
    nameError,
  ]);

  const handleUpdateName = () => {
    if (!newName.trim()) return;
    dispatch(updateCustomerName({ customer_id: id, name: newName.trim() }));
  };

  const handleGenerateStatement = async () => {
    const token = userInfo?.token?.access_token;
    if (!token) {
      toast.error("SESSION EXPIRED. PLEASE LOG IN AGAIN", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    try {
      setStatementLoading(true);
      const response = await axios.post(
        `${BASE_API_URL}/admin/customer/statement`,
        { customer_id: id, from_date: formatDate(statementFromDate), to_date: formatDate(statementToDate) },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `statement_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      let message = "Failed to generate statement";
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || message;
        } catch {}
      } else {
        message = error?.response?.data?.message || message;
      }
      toast.error(message, { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setStatementLoading(false);
    }
  };

  const setAccountOfficer = (admin) => {
    const reqData = {
      customer_id: id,
      admin_id: admin,
    };
    dispatch(assignOfficerToCustomer(reqData));
  };

  const deactivateCust = () => {
    setIsOpen(true);
  };

  const activateCustomer = () => {
    const postData = {
      customer_id: id,
      account_locked_until: null,
    };

    dispatch(customerDeactivate(postData));
  };

  const deactivateCard = (remove, id) => {
    const postData = {
      cardid: id,
      remove,
    };
    dispatch(removeCustomerCard(postData));
  };
  const restoreLim = (e) => {
    e.preventDefault();
    const reqData = {
      customer_name: customer?.data?.customer_information?.username,
      reset_limit: true,
    };

    console.log(reqData);

    dispatch(setCustomerLimit(reqData));
  };

  const handleRestrictionSubmit = async (event) => {
    event.preventDefault();

    const customerId = Number(id);
    if (!customerId || Number.isNaN(customerId)) {
      toast.error("INVALID CUSTOMER ID", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    if (Number(restrictionType) === 1 && liftRestriction) {
      toast.error("UNBLOCK IS NOT ALLOWED", { position: toast.POSITION.TOP_RIGHT });
      return;
    }

    const token = userInfo?.token?.access_token;
    if (!token) {
      toast.error("SESSION EXPIRED. PLEASE LOG IN AGAIN", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    try {
      setRestrictionLoading(true);
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const payload = {
        customer_id: customerId,
        type: Number(restrictionType),
        lift_restriction: liftRestriction,
      };

      const { data } = await axios.post(
        `${BASE_API_URL}/admin/customer/pnd-block`,
        payload,
        config
      );
      toast.success(data?.message || "RESTRICTION UPDATED", {
        position: toast.POSITION.TOP_RIGHT,
      });
      dispatch(getCustomerDetails(id));
    } catch (error) {
      toast.error(error?.response?.data?.message || "FAILED TO UPDATE RESTRICTION", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setRestrictionLoading(false);
    }
  };
  return (
    <>
      <ToastContainer />
      <SideNav />
      <Helmet>
        <title> Transactions | PennyTree - Admin</title>
      </Helmet>
      <section className="page-container">
        <div className="page-content">
          <Header />
          {loading || officerLoading ? (
            <Loader />
          ) : (
            <div className="page-body">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div className="profile-box">
                  <div className="profile-content">
                    <FontAwesomeIcon icon={faUserTie} />
                    <span>
                      {customer?.data?.customer_information?.full_name}
                    </span>
                  </div>
                  <div className="profile-content">
                    <FontAwesomeIcon icon={faPhone} />
                    <span>
                      {customer?.data?.customer_information?.mobile_number}
                    </span>
                  </div>
                  <div className="profile-content">
                    <FontAwesomeIcon icon={faEnvelope} />
                    <span>
                      {customer?.data?.customer_information?.email_address}
                    </span>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  {canUpdateCustomerNames && (
                    <button
                      className="withdrawal-btn"
                      onClick={() => setNameModal(true)}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Update Name
                    </button>
                  )}
                  {canGenerateStatement && (
                    <button
                      className="withdrawal-btn"
                      onClick={() => setStatementModal(true)}
                      style={{ background: "#4caf50", whiteSpace: "nowrap" }}
                    >
                      Generate Statement
                    </button>
                  )}
                </div>

                {isOpen && (
                  <CustomerDeactivateModal setIsOpen={setIsOpen} id={id} />
                )}

                {/* Update Name Modal */}
                {nameModal && (
                  <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                    zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
                  }}>
                    <div style={{
                      background: "#1a1a1a", borderRadius: "10px", width: "100%", maxWidth: "420px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid #2a2a2a" }}>
                        <h3 style={{ margin: 0, fontSize: "16px", color: "#fff" }}>Update Customer Name</h3>
                        <button onClick={() => { setNameModal(false); setNewName(""); }}
                          style={{ background: "transparent", border: "none", fontSize: "20px", cursor: "pointer", color: "#aaa", lineHeight: 1 }}>×</button>
                      </div>
                      <div style={{ padding: "24px" }}>
                        <p style={{ color: "#aaa", fontSize: "13px", marginBottom: "16px" }}>
                          Current name: <strong style={{ color: "#fff" }}>{customer?.data?.customer_information?.full_name || "—"}</strong>
                        </p>
                        <div style={{ marginBottom: "20px" }}>
                          <label style={{ display: "block", color: "#aaa", fontSize: "12px", marginBottom: "6px" }}>New Name</label>
                          <input
                            type="text"
                            placeholder="Enter new customer name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            style={{
                              width: "100%", background: "#2a2a2a", border: "1px solid #444",
                              borderRadius: "8px", padding: "10px 12px", color: "#fff",
                              fontSize: "14px", boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <button
                          className="login-btn"
                          onClick={handleUpdateName}
                          disabled={nameLoading || !newName.trim()}
                          style={{ width: "100%" }}
                        >
                          {nameLoading ? "Updating..." : "Update Name"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generate Statement Modal */}
                {statementModal && (
                  <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                    zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
                  }}>
                    <div style={{
                      background: "#1a1a1a", borderRadius: "10px", width: "100%", maxWidth: "420px",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid #2a2a2a" }}>
                        <h3 style={{ margin: 0, fontSize: "16px", color: "#fff" }}>Generate Statement</h3>
                        <button onClick={() => setStatementModal(false)}
                          style={{ background: "transparent", border: "none", fontSize: "20px", cursor: "pointer", color: "#aaa", lineHeight: 1 }}>×</button>
                      </div>
                      <div style={{ padding: "24px" }}>
                        <p style={{ color: "#aaa", fontSize: "13px", marginBottom: "16px" }}>
                          Customer: <strong style={{ color: "#fff" }}>{customer?.data?.customer_information?.full_name || "—"}</strong>
                        </p>
                        <div style={{ marginBottom: "16px" }}>
                          <label style={{ display: "block", color: "#aaa", fontSize: "12px", marginBottom: "6px" }}>From Date</label>
                          <DatePicker
                            selected={statementFromDate}
                            onChange={(date) => setStatementFromDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="From date"
                            className="form-control"
                            wrapperClassName="datepicker-full-width"
                          />
                        </div>
                        <div style={{ marginBottom: "24px" }}>
                          <label style={{ display: "block", color: "#aaa", fontSize: "12px", marginBottom: "6px" }}>To Date</label>
                          <DatePicker
                            selected={statementToDate}
                            onChange={(date) => setStatementToDate(date)}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="To date"
                            className="form-control"
                            wrapperClassName="datepicker-full-width"
                          />
                        </div>
                        <button
                          className="login-btn"
                          onClick={handleGenerateStatement}
                          disabled={statementLoading}
                          style={{ width: "100%", background: "#4caf50", borderColor: "#4caf50" }}
                        >
                          {statementLoading ? "Generating..." : "Generate PDF"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {canViewCustomerActions && (
                  <>
                    <div
                      className="account-officer"
                      style={{ display: "flex" }}
                    >
                      <select
                        style={{
                          background: "#1a1a1a",
                          border: "none",
                          padding: "13px",
                          width: "200px",
                          borderRadius: "10px",
                        }}
                        onChange={(e) => setAccountOfficer(e.target.value)}
                      >
                        <option>
                          {customer?.data?.customer_information
                            ?.account_officer !== null ? (
                            <span>ReAssign Account Officer</span>
                          ) : (
                            <span>Assign Account Officer</span>
                          )}
                        </option>
                        {success &&
                          officers &&
                          officers?.items.map((officer) => (
                            <option value={officer?.id}>{officer?.name}</option>
                          ))}
                      </select>

                      {customer?.data?.customer_information
                        ?.account_locked_until === null ? (
                        <div
                          className="status-container"
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            paddingLeft: "10px",
                          }}
                        >
                          <div className="button">
                            <FontAwesomeIcon />
                            <span onClick={deactivateCust}>
                              DeActivate Customer
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="status-container"
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            paddingLeft: "10px",
                          }}
                        >
                          <div className="button">
                            <FontAwesomeIcon />
                            {deactivateLoading ? (
                              <Loader />
                            ) : (
                              <span onClick={activateCustomer}>
                                Activate Customer
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {customer?.data?.customer_information?.account_officer !==
              null ? (
                <p
                  style={{
                    color: "green",
                    letterSpacing: "1.2px",
                    fontSize: "13px",
                  }}
                >
                  Account Officer -{" "}
                  {customer?.data?.customer_information?.account_officer}
                </p>
              ) : null}
              <div className="accordion user-restriction-accordion" id="userRestrictionAccordion">
                <div className="user-restriction-box accordion-item">
                  <h3 className="accordion-header" id="userRestrictionHeader">
                    <button
                      className="accordion-button user-restriction-box__toggle"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#userRestrictionCollapse"
                      aria-expanded="true"
                      aria-controls="userRestrictionCollapse"
                    >
                      <div className="user-restriction-box__top">
                        <h6>Restriction Control</h6>
                        <div className="user-restriction-box__badges">
                          {isPnd && (
                            <span className="user-status-badge user-status-badge--pnd">PND</span>
                          )}
                          {isBlocked && (
                            <span className="user-status-badge user-status-badge--blocked">BLOCKED</span>
                          )}
                          {!isPnd && !isBlocked && (
                            <span className="user-status-badge user-status-badge--normal">NORMAL</span>
                          )}
                        </div>
                      </div>
                    </button>
                  </h3>
                  <div
                    id="userRestrictionCollapse"
                    className="accordion-collapse collapse show"
                    aria-labelledby="userRestrictionHeader"
                  >
                    <div className="accordion-body user-restriction-box__accordion-body">
                      <form
                        className="user-restriction-box__form"
                        onSubmit={handleRestrictionSubmit}
                      >
                        <div className="user-restriction-box__field">
                          <label htmlFor="restrictionType">Restriction Type</label>
                          <select
                            id="restrictionType"
                            value={restrictionType}
                            onChange={(event) => {
                              const nextType = Number(event.target.value);
                              setRestrictionType(nextType);
                              if (nextType === 1) setLiftRestriction(false);
                            }}
                          >
                            <option value={0}>PND</option>
                            <option value={1}>BLOCK</option>
                          </select>
                        </div>
                        <div className="user-restriction-box__checkbox">
                          <input
                            id="liftRestriction"
                            type="checkbox"
                            checked={liftRestriction}
                            disabled={Number(restrictionType) === 1}
                            onChange={(event) => setLiftRestriction(event.target.checked)}
                          />
                          <label htmlFor="liftRestriction">Lift Restriction</label>
                        </div>
                        <button type="submit" disabled={restrictionLoading}>
                          {restrictionLoading ? "Submitting..." : "Submit"}
                        </button>
                      </form>
                      {Number(restrictionType) === 1 && (
                        <p className="user-restriction-box__note">
                          Blocked accounts cannot be unblocked from this control.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="row">
                    <div
                      className="col-12 d-flex flex-row"
                      style={{ justifyContent: "space-between" }}
                    >
                      <div className="content-box">
                        <div className="content-title">Account Information</div>
                        <div className="content-main">
                          <p>
                            PennTag:{" "}
                            <span>
                              {customer?.data?.customer_information?.username}
                            </span>
                          </p>
                          <p>
                            Merchant ID:{" "}
                            <span>
                              {
                                customer?.data?.customer_information
                                  ?.merchant_id
                              }
                            </span>
                          </p>
                          <p>
                            Individual Account No:{" "}
                            <span>
                              {
                                customer?.data?.customer_information
                                  ?.customer_account_number
                              }
                            </span>
                          </p>
                          <p>
                            Business Account No:{" "}
                            <span>
                              {
                                customer?.data?.customer_information
                                  ?.business_account_number
                              }
                            </span>
                          </p>
                          <p>
                            Account Open Date:{" "}
                            <span>
                              {customer?.data?.customer_information?.open_date?.substring(
                                0,
                                10
                              )}
                            </span>
                          </p>
                          <p>
                            Account Officer:{" "}
                            <span>
                              {
                                customer?.data?.customer_information
                                  ?.account_officer
                              }
                            </span>
                          </p>
                          <p>
                            Last Login Date:{" "}
                            <span>
                              {customer?.data?.customer_information?.last_login_date?.substring(
                                0,
                                10
                              )}
                            </span>
                          </p>
                          <p>
                            Average Monthly Logins:{" "}
                            <span>
                              {
                                customer?.data?.customer_information
                                  ?.monthly_logins
                              }
                            </span>
                          </p>
                          <p>
                            Total Transaction Count:{" "}
                            <span>
                              {
                                customer?.data?.customer_information
                                  ?.transaction_count
                              }
                            </span>
                          </p>
                        </div>
                        <br />
                        {limitLoading ? (
                          <Loader />
                        ) : (
                          <div
                            className="button"
                            style={{ width: "fit-content" }}
                          >
                            <FontAwesomeIcon />
                            <span onClick={restoreLim}>Restore Limit</span>
                          </div>
                        )}
                      </div>

                      <div className="content-box">
                        <div className="content-title">Balances</div>
                        <div className="content-main">
                          <p>
                            Individual Account Balance:{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.individual_wallet_balance
                              )}
                            </span>
                          </p>
                          <p>
                            Merchant Account Balance:{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.business_wallet_balance
                              )}
                            </span>
                          </p>
                          <p>
                            Total Transaction Value Individual (Inflow):{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.individual_inflow_transaction_count
                              )}
                            </span>
                          </p>
                          <p>
                            Total Transaction Value Individual (Outflow):{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.individual_outflow_transaction_count
                              )}
                            </span>
                          </p>
                          <p>
                            Total Transaction Value Merchant (Inflow):{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.merchant_inflow_transaction_count
                              )}
                            </span>
                          </p>
                          <p>
                            Total Transaction Value Merchant (Outflow):{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.merchant_outflow_transaction_count
                              )}
                            </span>
                          </p>
                          <p>
                            Total Transaction Value{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.total_transaction_value
                              )}
                            </span>
                          </p>
                          <p>
                            Active Investment Value{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.active_investment_value
                              )}
                            </span>
                          </p>
                          <p>
                            Last Seven Days Inflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.seven_days_inflow
                              )}
                            </span>
                          </p>
                          <p>
                            Last Seven Days Outflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.seven_days_outflow
                              )}
                            </span>
                          </p>
                          <p>
                            Last Thirty Days inflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.thirty_days_inflow
                              )}
                            </span>
                          </p>
                          <p>
                            Last Thirty Days Outflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.thirty_days_outflow
                              )}
                            </span>
                          </p>
                          <p>
                            Inflow Today{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.inflow_today
                              )}
                            </span>
                          </p>
                          <p>
                            Outflow Today
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.outflow_today
                              )}
                            </span>
                          </p>

                          <p>
                            Last P2p Seven Days Inflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.p2p_seven_days_inflow
                              )}
                            </span>
                          </p>
                          <p>
                            Last P2p Seven Days Outflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.p2p_seven_days_outflow
                              )}
                            </span>
                          </p>
                          <p>
                            Last P2p Thirty Days inflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.p2p_thirty_days_inflow
                              )}
                            </span>
                          </p>
                          <p>
                            Last P2p Thirty Days Outflow{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.p2p_thirty_days_outflow
                              )}
                            </span>
                          </p>
                          <p>
                            P2p Inflow Today{" "}
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.p2p_inflow_today
                              )}
                            </span>
                          </p>
                          <p>
                            P2p Outflow Today
                            <span>
                              {format(
                                customer?.data?.customer_information
                                  ?.p2p_outflow_today
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="row">
                    <div className="col-12">
                      <h6>Personal Vault(s)</h6>
                      <div
                        className="content-box"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto auto",
                          gap: "30px",
                        }}
                      >
                        {customer?.data?.personal_vaults?.map((vault, i) => (
                          <div className="content-main" key={i}>
                            <div
                              className="content-title"
                              style={{
                                textTransform: "uppercase",
                                fontWeight: "bold",
                                fontSize: "20px",
                                letterSpacing: "1.2px",
                              }}
                            >
                              {vault?.investment_name}
                            </div>

                            <p>
                              Amount:{" "}
                              <span>{format(vault?.current_amount)}</span>
                            </p>
                            <p>
                              Vault Maturity Date:{" "}
                              <span>{vault?.end_date?.substring(0, 10)}</span>
                            </p>
                            <p>
                              Interest Rate: <span>{vault?.interest}</span>
                            </p>
                            <p>
                              Interest Accrued{" "}
                              <span>{format(vault?.interest_accrued)}</span>
                            </p>
                            <p>
                              Duration: <span>{vault?.duration}</span>
                            </p>
                            <p>
                              Topup Amount:{" "}
                              <span>{format(vault?.topup_amount)}</span>
                            </p>
                            <p>
                              Rollover: <span>{vault?.roll_over}</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Target savings start */}

                <div className="col-lg-12">
                  <div className="row">
                    <div className="col-12">
                      <h6>Target Saving(s)</h6>
                      <div
                        className="content-box"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto auto",
                          gap: "30px",
                        }}
                      >
                        {savingsLoading ? (
                          <Loader />
                        ) : (
                          savings?.items?.map((vault, i) => (
                            <div className="content-main" key={i}>
                              <div
                                className="content-title"
                                style={{
                                  textTransform: "uppercase",
                                  fontWeight: "bold",
                                  fontSize: "20px",
                                  letterSpacing: "1.2px",
                                }}
                              >
                                {vault?.purpose}
                              </div>
                              <p>
                                Target Amount:{" "}
                                <span>{format(vault?.target_amount)}</span>
                              </p>
                              <p>
                                Date Created:{" "}
                                <span>
                                  {vault?.date_created?.substring(0, 10)}
                                </span>
                              </p>
                              <p>
                                Debit Amount:{" "}
                                <span>{format(vault?.debit_amount)}</span>
                              </p>
                              <p>
                                Start Date:{" "}
                                <span>
                                  {vault?.start_date?.substring(0, 10)}
                                </span>
                              </p>
                              <p>
                                Last Debit Date:{" "}
                                <span>
                                  {vault?.last_collection_date?.substring(
                                    0,
                                    10
                                  )}
                                </span>
                              </p>
                              <p>
                                Next Debit Date:{" "}
                                <span>
                                  {vault?.next_collection_date?.substring(
                                    0,
                                    10
                                  )}
                                </span>
                              </p>
                              <p>
                                Amount Saved:{" "}
                                <span>{format(vault?.amount_saved)}</span>
                              </p>
                              <p>
                                Expected Amount:{" "}
                                <span>{format(vault?.expected_amount)}</span>
                              </p>
                              <p>
                                Frequency: <span>{vault?.frequency}</span>
                              </p>
                              <button className="withdrawal-btn">
                                <Link
                                  to={`/target-savings/logs/${vault?.purpose}`}
                                  state={{
                                    customerId: vault?.id,
                                    savingsId: vault?.savings_id,
                                    name:
                                      vault?.first_name +
                                      " " +
                                      vault?.last_name,
                                    purpose: vault?.purpose,
                                  }}
                                >
                                  View Logs
                                </Link>
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Target Savings end */}

                {/* Customer Business Start */}
                <div className="col-lg-12">
                  <div className="row">
                    <div className="col-12">
                      <h6>Business</h6>
                      <div
                        className="content-box"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto auto",
                          gap: "30px",
                        }}
                      >
                        {businessLoading ? (
                          <Loader />
                        ) : (
                          business?.businesses?.map((b, i) => (
                            <div className="content-main" key={i}>
                              <div
                                className="content-title"
                                style={{
                                  textTransform: "uppercase",
                                  fontWeight: "bold",
                                  fontSize: "20px",
                                  letterSpacing: "1.2px",
                                }}
                              >
                                {b?.business_name}
                              </div>
                              <p>
                                Business Email Address:{" "}
                                <span>{b.support_email_address}</span>
                              </p>
                              <p>
                                Business Phone Number:{" "}
                                <span>{b.support_mobile_number}</span>
                              </p>
                              <p>
                                Business Tag: <span>{b.merchant_tag}</span>
                              </p>
                              <p>
                                Business Legal Name: <span>{b.legal_name}</span>
                              </p>
                              <p>
                                Business Address:{" "}
                                <span>{b.smile_id_address}</span>
                              </p>
                              <p>
                                Business Type:{" "}
                                <span>{b.business_type_other}</span>
                              </p>
                              <p>
                                Business Status: <span>{b.status}</span>
                              </p>

                              <BusinessWallet
                                customerid={id}
                                businessid={b.id}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Customer Business End */}
                {canViewCustomerActions && (
                  <div>
                    <h6>Cards</h6>
                    <div
                      className="col-lg-12"
                      style={{
                        display: "flex",
                        marginBottom: "20px",
                      }}
                    >
                      {!loadingCard &&
                        successCard &&
                        cards?.cards?.map((c) => (
                          <div id="card" class="cc">
                            <div id="main" class="mc">
                              <div id="ctyp" align="left">
                                <font class="hf">PENNYTREE</font>
                              </div>
                            </div>

                            <div class="cno" align="center">
                              <pre>{c?.card_number}</pre>
                            </div>
                            <div class="exp" align="center">
                              EXPIRY {c?.expires}
                            </div>
                            <div class="nv">
                              <div class="name" align="left">
                                {c?.gateway_provider}({c?.card_type})
                              </div>
                            </div>
                            <div class="nv">
                              <div class="name" align="center">
                                {c?.is_active ? "Activated" : "DeActivated"}
                              </div>
                            </div>

                            <select
                              style={{
                                background: "#1a1a1a",
                                border: "none",
                                padding: "13px",
                                width: "350px",
                                borderRadius: "10px",
                              }}
                              onChange={(e) =>
                                deactivateCard(e.target.value, c?.id)
                              }
                            >
                              {deleteCardLoading ? null : (
                                <>
                                  <option>
                                    <span>DeActivate Card</span>
                                  </option>

                                  <option value={"no"}>DeActivate</option>
                                  <option value={"activate"}>Activate</option>
                                  <option value={"yes"}>Remove</option>
                                </>
                              )}
                            </select>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                <div className="col-lg-12">
                  <div className="row">
                    <div className="col-12">
                      <h6>Last 300 Transactions</h6>
                      <CustomerTransactions
                        customeremail={
                          customer?.data?.customer_information?.username
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default UserScreen;
